import { isAccessTokenExpired } from './token-expiry'

const KEY_ACCESS = 'amglobal_workspace_access_token'
const KEY_REFRESH = 'amglobal_workspace_refresh_token'
const KEY_SESSION = 'amglobal_workspace_session_id'
const KEY_DEVICE = 'amglobal_workspace_device_id'
const KEY_USER = 'amglobal_workspace_user'

export type StoredAuthUser = {
  id: string
  email: string
  userType: string
  capabilities: string[]
  sessionVersion: number
  isApplicantSealed: boolean
}

export type WorkspaceAuthSession = {
  accessToken: string
  refreshToken: string
  sessionId: string
  deviceId: string
  user: StoredAuthUser
}

function readUser(raw: string | null): StoredAuthUser | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredAuthUser
    if (!parsed?.id && !parsed?.email) return null
    return parsed
  } catch {
    return null
  }
}

export function getWorkspaceAuthSession(): WorkspaceAuthSession | null {
  if (typeof localStorage === 'undefined') return null
  const accessToken = localStorage.getItem(KEY_ACCESS)?.trim() ?? ''
  const refreshToken = localStorage.getItem(KEY_REFRESH)?.trim() ?? ''
  const sessionId = localStorage.getItem(KEY_SESSION)?.trim() ?? ''
  const deviceId = localStorage.getItem(KEY_DEVICE)?.trim() ?? ''
  const user = readUser(localStorage.getItem(KEY_USER))
  if (!accessToken || !refreshToken || !sessionId || !user) return null
  return { accessToken, refreshToken, sessionId, deviceId, user }
}

/** Access token when present and not expired; keeps refresh credentials when expired. */
export function getValidAccessToken(): string | null {
  const session = getWorkspaceAuthSession()
  if (!session) return null
  if (isAccessTokenExpired(session.accessToken)) return null
  return session.accessToken
}

/** True when persisted login material exists (access token may be expired). */
export function hasWorkspaceAuthSession(): boolean {
  return getWorkspaceAuthSession() != null
}

export function getRefreshCredentials(): {
  refreshToken: string
  sessionId: string
} | null {
  const session = getWorkspaceAuthSession()
  if (!session) return null
  return {
    refreshToken: session.refreshToken,
    sessionId: session.sessionId,
  }
}

export function setWorkspaceAuthSession(session: WorkspaceAuthSession): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(KEY_ACCESS, session.accessToken)
  localStorage.setItem(KEY_REFRESH, session.refreshToken)
  localStorage.setItem(KEY_SESSION, session.sessionId)
  localStorage.setItem(KEY_DEVICE, session.deviceId)
  localStorage.setItem(KEY_USER, JSON.stringify(session.user))
}

export function clearWorkspaceAuthSession(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem(KEY_ACCESS)
  localStorage.removeItem(KEY_REFRESH)
  localStorage.removeItem(KEY_SESSION)
  localStorage.removeItem(KEY_DEVICE)
  localStorage.removeItem(KEY_USER)
}
